import {Injectable} from "@angular/core";
// import {MatPaginator} from "@angular/material/paginator";
import {MatLegacyPaginator as MatPaginator} from "@angular/material/legacy-paginator";
import {UsageFilter} from "../usage/usage.filter";
import {IFilter} from "./IFilter";
import {SmsMoFilter} from "../so/smsmo.filter";
import {debounceTime} from "rxjs/operators";
import {Subscription} from "rxjs";
import {PersistenceGeneralSettings} from "./PersistenceGeneralSettings";

/*
 * Service for persisting settings across sessions
 * Usage:
 * 1. Add module property to your component:
      pageSettings: SettingsPersistenceService;
 * 2. Setup in ngOnInit using either sessionStorage or localStorage:
      this.pageSettings = new SettingsPersistenceService(storage, "storage_key");
      this.pageSettings
        .setGeneralSettings(this.settings)            // for page specific settings like page size, auto refresh, etc.
        .setDateRangeSelector(this.dateRangeSelector) // if needed
        .setFilter(this.filter)                       // page filter object
        .setPaginator(this.paginator)                 // page paginator object
        .setEmbedded(this.embedded)                   // if true, the service will not save settings to storage
        .setEmbeddedFilterProps(this.embeddedFilterProps) // if embedded is true, the service will use this object to initialize filter
        .init();
 * 3. use default page size from pageSettings.defaultPageSize (typically in HttpParams)
      let params:HttpParams = this.filter.getParams()
         .set("page", '' + (this.paginator.pageIndex || 0))
         .set("size", '' + (this.paginator.pageSize || this.pageSettings.defaultPageSize))
 * 4. Simplify paginator parameters HTML (the service will automatically link the pageIndex,
 *    pageSize and pageSizeOptions to the paginator):
      <mat-paginator #paginator
          (page)="reload()">
      </mat-paginator>
 * 5. Update filter to implement IFilter interface
 */

@Injectable()
export class SettingsPersistenceService {
  private storage:Storage;
  private storageKey: string;

  private filterSubscription?:Subscription;
  private dateRangeSubscription?:Subscription;
  private paginatorSubscription?:Subscription;
  private generalSettingsSubscription?:Subscription;

  private _generalSettings = new PersistenceGeneralSettings();
  private _paginator?:MatPaginator;
  private _dateRangeSelector?:UsageFilter;
  private _filter?:IFilter;
  private _embedded:boolean = false;
  private _embeddedFilterProps?:object;
  private _defaultPageSize:number = 25;
  private _defaultPageSizes:number[] = [25, 50, 100, 250];

  constructor(storage: Storage, storageKey:string) {
    this.storage = storage;
    this.storageKey = storageKey;
  }

  get generalSettings():PersistenceGeneralSettings {
    return this._generalSettings;
  }
  set generalSettings(settings:PersistenceGeneralSettings) {
    this._generalSettings = settings;
  }

  setGeneralSettings(settings:any): this {
    this.generalSettings = settings;
    return this;
  }

  get paginator():MatPaginator {
    return this._paginator;
  }
  set paginator(paginator:MatPaginator) {
    this._paginator = paginator;
  }
  setPaginator(paginator:MatPaginator): this {
    this.paginator = paginator;
    return this;
  }

  get dateRangeSelector():UsageFilter {
    return this._dateRangeSelector;
  }
  set dateRangeSelector(dateRangeSelector:UsageFilter) {
    this._dateRangeSelector = dateRangeSelector;
  }
  setDateRangeSelector(dateRangeSelector:UsageFilter): this {
    this.dateRangeSelector = dateRangeSelector;
    return this;
  }

  get filter():any {
    return this._filter;
  }
  set filter(filter:any) {
    this._filter = filter;
  }
  setFilter(filter:IFilter): this {
    this.filter = filter;
    return this;
  }

  get embedded():boolean {
    return this._embedded;
  }
  set embedded(embedded:boolean) {
    this._embedded = embedded;
  }
  setEmbedded(embedded:boolean): this {
    this.embedded = embedded;
    return this;
  }

  get embeddedFilterProps():any {
    return this._embeddedFilterProps;
  }
  set embeddedFilterProps(embeddedFilterProps:any) {
    this._embeddedFilterProps = embeddedFilterProps;
  }
  setEmbeddedFilterProps(embeddedFilterProps:any): this {
    this.embeddedFilterProps = embeddedFilterProps;
    return this;
  }

  get defaultPageSize():number {
    return this._defaultPageSize;
  }
  set defaultPageSize(defaultPageSize:number) {
    this._defaultPageSize = defaultPageSize;
  }
  setDefaultPageSize(defaultPageSize:number): this {
    this.defaultPageSize = defaultPageSize;
    return this;
  }
  get defaultPageSizes():number[] {
    return this._defaultPageSizes;
  }
  set defaultPageSizes(defaultPageSizes:number[]) {
    this._defaultPageSizes = defaultPageSizes;
  }
  setDefaultPageSizes(defaultPageSizes:number[]): this {
    this.defaultPageSizes = defaultPageSizes;
    return this;
  }

  init():void {
    if (this.paginator) {
      this.paginator.pageIndex = 0;
      this.paginator.pageSize = this.defaultPageSize;
      this.paginator.pageSizeOptions = this.defaultPageSizes;
    }
    this.loadSettings();
  }

  private hasProperties(value: any): boolean {
    return (
      value !== null &&
      typeof value === "object" &&
      Object.keys(value).length > 0
    );
  }

  loadSettings() {
    this.removeFilterListener();
    this.removeDateRangeListener();
    this.removePaginatorListener();
    this.removeGeneralSettingsListener();

    if (this.embedded) {
      if (this.embedded && this.hasProperties(this.embeddedFilterProps))
        this.filter.setFilters(this.embeddedFilterProps);
      return;
    }

    let storageSettingsJson = this.storage.getItem(this.storageKey);

    if (storageSettingsJson) {
      let storageSettings = JSON.parse(storageSettingsJson);
      this.generalSettings.setSettings(storageSettings.settings);
      this.generalSettings.pageIndex = storageSettings.settings?.pageIndex || 0;
      this.generalSettings.pageSize = storageSettings.settings?.pageSize || this.defaultPageSize;
      this.addGeneralSettingsListener();

      if (this.paginator) {
        this.paginator.pageIndex = this.generalSettings.pageIndex || 0;
        this.paginator.pageSize = this.generalSettings.pageSize || this.defaultPageSize;
        this.addPaginatorListener();
      }

      if (this.filter) {
        this.filter.setFilters(storageSettings?.filter ?? {});
        this.addFilterListener();
      }

      if (this.dateRangeSelector) {
        this.dateRangeSelector.setDateRanges(storageSettings.dateRange ?? {});
        this.addDateRangeListener();
      }

    } else {

      this.generalSettings.pageIndex = 0;
      this.generalSettings.pageSize = this.defaultPageSize;
      this.addGeneralSettingsListener();

      if (this.paginator) {
        this.paginator.pageIndex = 0;
        this.paginator.pageSize = this.defaultPageSize;
        this.paginator.pageSizeOptions = this.defaultPageSizes;
        this.addPaginatorListener();
      }

      if (this.filter) {
        this.filter.clear();
        this.addFilterListener();
      }

      if (this.dateRangeSelector) {
        this.dateRangeSelector.clear();
        this.addDateRangeListener();
      }
    }
  }

  saveSettings() {
    if (this.embedded) return;

    let storageSettings = {
      filter: this.filter?.toObj(),
      settings: this.generalSettings?.toObj(),
      dateRange: this.dateRangeSelector?.toObj(),
    };
    this.storage.setItem(this.storageKey, JSON.stringify(storageSettings));
  }

  addFilterListener() {
    if (!this.filter) return;
    this.removeFilterListener();
    this.filterSubscription = this.filter.anyChange.pipe(debounceTime(500)).subscribe(change => {
      if (this.paginator) this.paginator.pageIndex = 0;
      this.saveSettings();
    });
  }

  removeFilterListener() {
    this.filterSubscription?.unsubscribe();
    this.filterSubscription = null;
  }

  addDateRangeListener() {
    if (!this.dateRangeSelector) return;
    this.removeDateRangeListener();
    this.dateRangeSubscription = this.dateRangeSelector.anyChange.pipe().pipe(debounceTime(500)).subscribe(change => {
      if (this.paginator) this.paginator.pageIndex = 0;
      this.saveSettings();
    });
  }
  removeDateRangeListener() {
    this.dateRangeSubscription?.unsubscribe();
    this.dateRangeSubscription = null;
  }

  addPaginatorListener() {
    if (!this.paginator) return;
    this.removePaginatorListener();
    this.paginatorSubscription = this.paginator.page.pipe().subscribe(page => {
      if (this.paginator.pageIndex !== this.generalSettings.pageIndex || this.paginator.pageSize !== this.generalSettings.pageSize) {
        this.generalSettings.pageIndex = this.paginator.pageIndex;
        this.generalSettings.pageSize = this.paginator.pageSize;
        this.saveSettings();
      }
    });
  }
  removePaginatorListener() {
    this.paginatorSubscription?.unsubscribe();
    this.paginatorSubscription = null;
  }


  private addGeneralSettingsListener() {
    if (!this.generalSettings) return;
    this.removeGeneralSettingsListener();
    this.generalSettingsSubscription = this.generalSettings.anyChange.pipe().subscribe(settings => {
      this.saveSettings();
    });

  }

  private removeGeneralSettingsListener() {
    this.generalSettingsSubscription?.unsubscribe();
    this.generalSettingsSubscription = null;
  }
}
